/**
 * useOfflineSync — hook orchestrating the sync queue drain loop and
 * exposing state (online / queueLen / syncing / conflicts) for the
 * status badge to consume.
 *
 * Feb 2026. Pairs with `utils/syncQueue.js`.
 *
 *   • `submit(config)` — the ONLY entry point for scan / complete
 *     writes on the Floor page. If online, tries a direct axios
 *     call. On network error, enqueues + returns a synthetic
 *     `{ ok: false, queued: true }`. If offline at call time,
 *     enqueues immediately without touching the network.
 *   • `drain()`         — attempts to flush the queue. Called
 *     automatically on `window.online` + on an interval when at
 *     least one item is pending.
 *   • Return shape: `{ online, syncing, queueLen, conflicts,
 *                       submit, drain }`.
 *
 * Conflict handling: server-side rejections in the 4xx range are
 * treated as PERMANENT conflicts (task was completed by someone
 * else, ASN gone, permission revoked, etc). The item is marked
 * `conflict` on the DB row and stops being retried — the badge
 * surfaces the count so the user can review + drop it manually.
 * Any other error (5xx, network) triggers a retry after
 * `bumpRetry`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

import { bumpRetry, enqueue, list, markConflict, remove, subscribe } from '../utils/syncQueue';

const RETRY_INTERVAL_MS = 20000;

async function performRequest(cfg) {
  return axios({
    method: cfg.method || 'post',
    url: cfg.url,
    data: cfg.body,
    headers: cfg.headers || {},
    // 8s hard timeout so the drain loop doesn't wedge on a stalled
    // TCP handshake.
    timeout: 8000,
  });
}

function isNetworkError(err) {
  // axios sets .response when the server responded; a network drop
  // has no .response and .message === 'Network Error'.
  return !!err && !err.response;
}

export default function useOfflineSync() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine !== false);
  const [syncing, setSyncing] = useState(false);
  const [rows, setRows] = useState([]);
  const drainInFlightRef = useRef(false);

  // Subscribe to queue mutations for the badge count.
  useEffect(() => {
    const unsub = subscribe((next) => setRows(next || []));
    return unsub;
  }, []);

  // Online/offline listeners.
  useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const drain = useCallback(async () => {
    if (drainInFlightRef.current) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    drainInFlightRef.current = true;
    setSyncing(true);
    try {
      const pending = (await list()).filter((r) => !r.conflict);
      for (const item of pending) {
        try {
          await performRequest(item);
          await remove(item.id);
        } catch (err) {
          if (isNetworkError(err)) {
            // Network gone again → stop draining, retry later. Do
            // NOT mark conflict.
            break;
          }
          const status = err.response?.status || 0;
          if (status >= 400 && status < 500) {
            // 4xx = permanent. Task probably already completed by
            // someone else, ASN cancelled, or permission revoked.
            await markConflict(
              item.id,
              err.response?.data?.detail || `HTTP ${status}`,
            );
            continue;
          }
          // 5xx → retry after backoff. Cap so we don't spin forever.
          await bumpRetry(item.id);
          break;
        }
      }
    } finally {
      drainInFlightRef.current = false;
      setSyncing(false);
    }
  }, []);

  // Auto-drain: on online, and on an interval when the queue isn't
  // empty. The interval only ticks if we're online AND have work.
  useEffect(() => {
    if (!online) return () => {};
    // Fire once immediately when we come back online.
    drain();
    const backlog = rows.some((r) => !r.conflict);
    if (!backlog) return () => {};
    const iv = setInterval(drain, RETRY_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [online, rows, drain]);

  const submit = useCallback(async (config) => {
    // Ensure fields the queue needs.
    const cfg = {
      method: 'post',
      body: null,
      headers: {},
      kind: 'generic',
      ...config,
    };
    // If we're offline, don't even attempt — go straight to queue.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      const item = await enqueue(cfg);
      return { queued: true, offline: true, id: item.id };
    }
    // Try live. On network error → queue transparently.
    try {
      const resp = await performRequest(cfg);
      return { queued: false, ok: true, data: resp.data, status: resp.status };
    } catch (err) {
      if (isNetworkError(err)) {
        const item = await enqueue(cfg);
        setOnline(false); // best-effort flip; the OS event will confirm
        return { queued: true, offline: true, id: item.id };
      }
      // Server rejected (4xx/5xx) — bubble to caller.
      throw err;
    }
  }, []);

  const queueLen = rows.filter((r) => !r.conflict).length;
  const conflicts = rows.filter((r) => !!r.conflict);

  return { online, syncing, queueLen, conflicts, submit, drain };
}
